/// Menu
//import Metismenu from "metismenujs";
import React, { useContext, useEffect, useReducer, useState } from "react";
/// Scroll
import PerfectScrollbar from "react-perfect-scrollbar";
import Collapse from 'react-bootstrap/Collapse';
/// Link
import { Link, useLocation } from "react-router-dom";
import { useScrollPosition } from "@n8tb1t/use-scroll-position";
import { ThemeContext } from "../../../context/ThemeContext";
import { ReactComponent as LogoPoweredBySportId } from "../../../images/powered-by-SportID.svg";

import { MenuStrutturaList } from './MenuStruttura';
import { useFunzioni } from '../../components/Util/featureFlags';
import { useMemberships } from '../../../hooks/useMemberships';
import { useProfile } from '../../../hooks/useProfile';
import { useAdminBadges } from '../../../hooks/useAdminBadges';
import { BadgeCount } from './BadgeCount';
import './sidebar-touch.css';

const reducer = (previousState, updatedState) => ({
  ...previousState,
  ...updatedState,
});

const initialState = {
  active: "",
  activeSubmenu: "",
}

const SideBar = () => {
  const {
    iconHover,
    sidebarposition,
    headerposition,
    sidebarLayout,
  } = useContext(ThemeContext);

  const [state, setState] = useReducer(reducer, initialState);
  /**
   * Quale sottomenù è stato aperto con un tocco, nella sidebar a sole icone.
   *
   * Sta separato da `state.active` perché quello lo imposta anche il percorso
   * corrente: riusarlo terrebbe il pannello della sezione in cui ci si trova
   * sempre aperto sopra il contenuto, invece di aprirsi solo quando lo si
   * chiede. Chi lo legge davvero è `sidebar-touch.css`, tramite la classe
   * `sub-open`.
   */
  const [sottomenuAperto, setSottomenuAperto] = useState('');
  const { profile } = useProfile();
  const { getBadgeCount } = useMemberships({ autoFetch: true });
  const badgeCount = getBadgeCount();
  const { unpaidCount, rejectedCount } = useAdminBadges({ autoFetch: true });
  const { purchaseLinks } = useFunzioni();
  const { menuItems } = MenuStrutturaList({ badgeCount, unpaidCount, rejectedCount, profile, linkAcquisto: purchaseLinks });
  const location = useLocation();
  const path = location.pathname;

  // For scroll
  const [hideOnScroll, setHideOnScroll] = useState(true);
  useScrollPosition(
    ({ prevPos, currPos }) => {
      const isShow = currPos.y > prevPos.y;
      if (isShow !== hideOnScroll) setHideOnScroll(isShow);
    },
    [hideOnScroll]
  );

  // Il badge count viene ora gestito automaticamente dal hook useMemberships

  /**
   * Come si identifica una voce di menù.
   *
   * Non con il titolo: quello di "La mia struttura" e` JSX, perche` ci sta dentro
   * l'icona di avviso, e un elemento React viene ricreato a ogni render. Confrontarlo
   * con `===` da` sempre falso, e la voce non si evidenziava mai. Le voci foglia hanno
   * un percorso, che e` stabile; i gruppi hanno un titolo che e` davvero una stringa.
   */
  const chiaveVoce = voce => voce.to ?? (typeof voce.title === 'string' ? voce.title : '');

  const handleMenuActive = status => {
    setState({ active: status });
    if (state.active === status) {
      setState({ active: "" });
    }
  }

  /**
   * Vero quando la sidebar è la striscia di sole icone del telefono: sotto i
   * 768px e con il menù non ancora aperto (vedi "SIDEBAR MOBILE" in
   * `_sportid-design-align.scss`).
   */
  const strisciaDiIcone = () => {
    const wrapper = document.getElementById('main-wrapper');
    return window.innerWidth <= 767 && wrapper !== null && !wrapper.classList.contains('menu-toggle');
  };

  const apriChiudiSottomenu = titolo => {
    // Nella striscia del telefono i sottomenù sono nascosti per scelta, e il
    // menù completo sta dietro l'hamburger: toccare un'icona che ha un
    // sottomenù non produceva niente, e niente diceva dove andare a cercarlo.
    // Ora quel tocco apre il menù completo con il sottomenù già aperto: un
    // gesto invece di due. La classe sul contenitore è la stessa che mette il
    // pulsante hamburger, quindi i due restano d'accordo.
    if (strisciaDiIcone()) {
      document.getElementById('main-wrapper').classList.add('menu-toggle');
      setSottomenuAperto(titolo);
      return;
    }

    setSottomenuAperto(precedente => (precedente === titolo ? '' : titolo));
  };

  /**
   * Clic su una voce con sottomenù: se è aperta si chiude, altrimenti si apre.
   * Prima i due stati (`active`, che segue anche la pagina corrente, e `sottomenuAperto`)
   * si invertivano ognuno per conto suo: sulla voce della pagina corrente il primo si
   * spegneva e il secondo si accendeva, quindi la freccia girava ma il menù restava aperto.
   */
  const apriChiudiVoce = chiave => {
    const aperta = state.active === chiave || sottomenuAperto === chiave;
    if (aperta && !strisciaDiIcone()) {
      setState({ active: '' });
      setSottomenuAperto('');
      return;
    }
    setState({ active: chiave });
    apriChiudiSottomenu(chiave);
  };

  const handleSubmenuActive = status => {
    setState({ activeSubmenu: status });
    if (state.activeSubmenu === status) {
      setState({ activeSubmenu: "" });
    }
  }

  // Cambiata pagina, il menù ha finito il suo compito: si chiude il pannello e,
  // sul telefono, si torna alla striscia di icone invece di restare aperti
  // sopra la pagina appena scelta.
  useEffect(() => {
    setSottomenuAperto('');
    const wrapper = document.getElementById('main-wrapper');
    if (window.innerWidth <= 767 && wrapper !== null) {
      wrapper.classList.remove('menu-toggle');
    }
  }, [path]);

  useEffect(() => {

    // Update active menu based on the current path
    // Si riparte da zero a ogni pagina: una voce non resta accesa su pagine che non sono sue (es. Account)
    if (path.endsWith('/account')) setState({ active: '' });
    menuItems.forEach(menu => {
      // Le voci foglia non hanno bisogno di una riga a testa qui sotto: hanno un
      // percorso, e basta confrontarlo. Le regole esplicite piu` avanti restano per i
      // gruppi e per i casi in cui l'evidenziazione deve reggere anche sulle
      // sottopagine, dove il percorso non coincide esattamente.
      if (!menu.content && menu.to && (path === menu.to || (menu.attivoSu || []).some(p => path.startsWith(p)))) {
        setState({ active: chiaveVoce(menu) });
      }
      if(menu.title==='Dashboard') {
        if(path==='/struttura/dashboard') {
          setState({ active: chiaveVoce(menu) });  
        }
      }
      if(menu.title==='La mia struttura') {
        if(path==='/struttura/lamiastruttura') {
          setState({ active: chiaveVoce(menu) });  
        }
      }
      if(menu.title==='Organismo sociale') {
        // Tutte le sue pagine, Consiglio direttivo e Assemblee compresi: se ne manca
        // una, standoci dentro la voce resta chiusa e sembra che il menu non segua.
        if(path.indexOf('/struttura/soci')>-1) {
          setState({ active: chiaveVoce(menu) });
        }
        if(path.indexOf('/struttura/tesserati')>-1) {
          setState({ active: chiaveVoce(menu) });
        }
        if(path.indexOf('/struttura/cda')>-1) {
          setState({ active: chiaveVoce(menu) });
        }
        if(path.indexOf('/struttura/assemblee')>-1) {
          setState({ active: chiaveVoce(menu) });
        }
        if(path.indexOf('/struttura/comingsoon')>-1) {
          setState({ active: chiaveVoce(menu) });
        }
      }
      if(menu.title==='Anagrafiche') {
        if(path.indexOf('/struttura/clienti')>-1) {
          setState({ active: chiaveVoce(menu) });
        }
        if(path.indexOf('/struttura/junior')>-1) {
          setState({ active: chiaveVoce(menu) });
        }
        if(path.indexOf('/struttura/teams')>-1) {
          setState({ active: chiaveVoce(menu) });
        }
      }
      if(menu.title==='Richieste') {
        if(path.indexOf('/struttura/richieste')>-1) {
          setState({ active: chiaveVoce(menu) });  
        }
      }
      if(menu.title==='DonAction') {
        if(path.indexOf('/struttura/referral')>-1) {
          setState({ active: chiaveVoce(menu) });  
        }
      }
      if(menu.title==='Comunicazioni') {
        if(path.indexOf('/struttura/comunicazioni')>-1) {
          setState({ active: chiaveVoce(menu) });  
        }
      }
      if(menu.title==='Documenti') {
        if(path==='/struttura/documenti') {
          setState({ active: chiaveVoce(menu) });  
        }
      }
      if(menu.title==='Impianti') {
        if(path.indexOf('/struttura/booking/facilities')>-1 ||
           path.indexOf('/struttura/booking/userFacilities')>-1 ||
           path.indexOf('/struttura/booking/facilities/calendar')>-1) {
          setState({ active: chiaveVoce(menu) });
        }
      }
      if(menu.title==='Maestri') {
        if(path.indexOf('/struttura/booking/lessons')>-1 ||
           path.indexOf('/struttura/booking/userLessons')>-1 ||
           path.indexOf('/struttura/booking/lessons/calendar')>-1 ||
           path.indexOf('/struttura/maestri')>-1) {
          setState({ active: chiaveVoce(menu) });
        }
      }
      if(menu.title==='Calendario') {
        if(path.indexOf('/struttura/calendario')>-1) {
          setState({ active: chiaveVoce(menu) });
        }
      }
      if(menu.title==='FitCoin') {
        if(path.indexOf('/struttura/fitcoin')>-1) {
          setState({ active: chiaveVoce(menu) });
        }
      }
      if(menu.title==='Strumenti') {
        if(path==='/struttura/strumenti/impostazioni') {
          setState({ active: chiaveVoce(menu) });
        }
      }
    });

  }, [path, badgeCount, unpaidCount, rejectedCount]);

  return (
    <div
      className={`dlabnav ${iconHover} ${
        sidebarposition.value === "fixed" &&
        sidebarLayout.value === "horizontal" &&
        headerposition.value === "static"
          ? hideOnScroll > 120
            ? "fixed"
            : ""
          : ""
      }`}
    >
      <PerfectScrollbar className="dlabnav-scroll d-flex flex-column justify-content-between">
        <ul className="metismenu" id="menu" style={{ overflow: 'show' }}>
          {menuItems.map((data, index) => {
            let menuClass = data.classsChange;
            if (menuClass === "menu-title") {
              return (
                <li className={menuClass} key={index}>{data.title}</li>
              );
            } else {
              return (
                <li className={` ${state.active === chiaveVoce(data) ? 'mm-active' : ''} ${sottomenuAperto === chiaveVoce(data) ? 'sub-open' : ''}`}
                  key={index}
                >
                  {data.content && data.content.length > 0 ?
                    <Link to={"#"}
                      className="has-arrow"
                      onClick={(e) => { e.preventDefault(); apriChiudiVoce(chiaveVoce(data)); }}
                    >
                      {data.iconStyle}
                      <span className="nav-text">
                        {data.title}
                        <BadgeCount count={data.badge} />
                      </span>
                    </Link>
                    :
                    <Link to={data.to}>
                      {data.iconStyle}
                      <span className="nav-text">
                        {data.title}
                        <BadgeCount count={data.badge} />
                      </span>
                    </Link>
                  }
                  <Collapse in={state.active === chiaveVoce(data) || sottomenuAperto === chiaveVoce(data)}>
                    {/* Lo stile in linea non e' un vezzo: nella sidebar a icone il tema
                        nasconde il sottomenu' con un `display: none` che nessuna classe
                        nostra puo' aggirare con certezza, perche' basta un aggiornamento
                        del tema per cambiare le carte in tavola. Scritto qui, vince e
                        basta. Il foglio sidebar-touch.css resta solo per dare al
                        pannello lo stesso aspetto che ha col mouse. */}
                    <ul
                      className={`${menuClass === "mm-collapse" ? "mm-show" : ""}`}
                      style={sottomenuAperto === data.title ? { display: 'block' } : undefined}
                    >
                      {data.content && data.content.map((data, index) => {
                        return (
                          <li key={index}
                            className={`${state.activeSubmenu === data.title ? "mm-active" : ""}`}
                          >
                            {data.content && data.content.length > 0 ?
                              <>
                                <Link to={data.to} className={data.hasMenu ? 'has-arrow' : ''}
                                  onClick={() => { handleSubmenuActive(data.title) }}
                                >
                                  {data.title}
                                </Link>
                                <Collapse in={state.activeSubmenu === data.title ? true : false}>
                                  <ul className={`${menuClass === "mm-collapse" ? "mm-show" : ""}`}>
                                    {data.content && data.content.map((data, index) => {
                                      return (
                                        <li key={index}>
                                          <Link to={data.to} className={`${path === data.to ? "mm-active" : ""}`}>{data.title}</Link>
                                        </li>
                                      )
                                    })}
                                  </ul>
                                </Collapse>
                              </>
                              :
                              (
                                <Link to={data.hasMenu ? '#' : data.to}>
                                  {data.iconStyle}
                                  <span className="nav-text">{data.title}</span>
                                </Link>
                              )
                            }
                          </li>
                        )
                      })}
                    </ul>
                  </Collapse>
                </li>
              )
            }
          })}
        </ul>
        <div className="sid-powered-by">
          <div className="sid-powered-by__label">Powered by <strong>SportID</strong></div>
          <div className="sid-powered-by__version">Versione: {process.env.REACT_APP_VERSION}</div>
        </div>
      </PerfectScrollbar>
    </div>
  );
};

export default SideBar;
